import { checkRefreshToken } from "./SpotifyAccountController";
import { loadAccessToken, loadLastAddedAlbumTime, loadTooManyRequestsBlocking } from "../utils/localStorageUtils";
import { buildFullItemId, getItemType } from "../utils/spotifyUtils";
import { timeout } from "../utils/utils";
import { CHECK_NEW_ALBUMS_BLOCK_LENGTH } from "../utils/constants/userSettingsConstants";
import { 
    CLEAR_QUEUE_DEFAULT_TIMEOUT_DELAY, 
    CLEAR_QUEUE_FLAG_TRACK_COUNT, 
    CLEAR_QUEUE_FLAG_TRACK_URI, 
    CLEAR_QUEUE_STOP_TIMEOUT_DELAY, 
    CLEAR_QUEUE_TRACK_COUNT_SAFETY_STOP 
} from "../utils/constants/otherConstants";
import { ITEM_TYPES } from "../utils/constants/enumConstants";
import { SPOTIFY_WEP_API_BASE_URL as BASE_URL } from "../utils/constants/connectionConstants";

const DEBUG = true;

// #region Complex methods

// #region Check Spotify premium account

const checkPremiumAccountRequest = () => {
    const endpoint = "/me";

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () =>  undefined;

    const method = 'GET';
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const checkPremiumAccount = () => {
    return new Promise((conclude) => {
        const request = checkPremiumAccountRequest();

        resolve(request)
        .then((result) => {
            conclude(result.display_name);
        })
        .catch(() => {
            conclude(false);
        })
    });
}

// #endregion Check Spotify premium account

// #region Clear queue

export const clearQueue = (pointedDeviceId, originalVolume, canModifyVolume) => {
    let foundFlag = false;

    const getTimeoutDelay = (trackCount) => {
        return trackCount === CLEAR_QUEUE_TRACK_COUNT_SAFETY_STOP ? CLEAR_QUEUE_STOP_TIMEOUT_DELAY : CLEAR_QUEUE_DEFAULT_TIMEOUT_DELAY;
    }
    
    return new Promise((resolve, reject) => {
        const postFlagItem = (count = 0) => {
            if(count === CLEAR_QUEUE_FLAG_TRACK_COUNT)
            {
                clearLoop(0);
                return;
            }

            postItemToQueue(CLEAR_QUEUE_FLAG_TRACK_URI, pointedDeviceId)
                .then(() => postFlagItem(count + 1))
                .catch((error) => reject(error));
        }
    
        const skipToNextItem = (trackIndex) => {
            postNextItem(pointedDeviceId)
                .then(() => {
                    checkCurrentSong(trackIndex);
                })
                .catch((error) => reject(error));
        }
    
        const checkCurrentSong = (trackIndex) => {
            timeout(getTimeoutDelay(trackIndex))
                .then(() => {
                    if(trackIndex >= CLEAR_QUEUE_TRACK_COUNT_SAFETY_STOP)
                    {
                        trackIndex = 0;
                    }
    
                    getPlaybackState()
                        .then((result) => {
                            const currentItemUri = result.item.uri;

                            if(currentItemUri !== CLEAR_QUEUE_FLAG_TRACK_URI)
                            {
                                if(foundFlag)
                                {
                                    putPausePlayback(pointedDeviceId)
                                        .then(() => {
                                            timeout(getTimeoutDelay(0))
                                                .then(() => {
                                                    canModifyVolume ?
                                                    putPlaybackVolume(originalVolume, pointedDeviceId)
                                                        .then(() => resolve())
                                                        .catch((error) => reject(error)) :
                                                    resolve()
                                                })
                                        })
                                        .catch((error) => reject(error))
                                }
                                else
                                {
                                    clearLoop(trackIndex + 1);
                                }

                            }
                            else
                            {
                                foundFlag = true;
                                clearLoop(trackIndex + 1);
                            }
                        })
                        .catch((error) => reject(error));
                })
        }
    
        const clearLoop = (trackIndex, addFlagItem = false) => {
            if(addFlagItem)
            {
                canModifyVolume ?
                putPlaybackVolume(0, pointedDeviceId)
                    .then(() => {
                        postFlagItem();
                    })
                    .catch((error) => reject(error)) :
                postFlagItem();
            }
            else
            {
                skipToNextItem(trackIndex);
            }
        }

        clearLoop(0, true);
    });
}

// #endregion Clear queue

// #region Album's first and last songs

export const getAlbumFirstSong = (albumId) => {
    return new Promise((resolve, reject) =>{
        getAlbumFirstAndLastSongs(albumId)
            .then((result) => {
                resolve(result.firstSong);
            })
            .catch((error) => reject(error));
    })
}

export const getAlbumLastSong = (albumId) => {
    return new Promise((resolve, reject) => {
        getAlbumFirstAndLastSongs(albumId)
            .then((result) => {
                resolve(result.lastSong);
            })
            .catch((error) => reject(error));
    })
}

export const getAlbumFirstAndLastSongs = (albumId) => {
    return new Promise((resolve, reject) => {
        getAlbum(albumId)
            .then((result) => {
                const tracks = result.tracks.items;
                resolve({
                    firstSong: tracks[0],
                    lastSong: tracks[tracks.length - 1]
                });
            })
            .catch((error) => reject(error));
    })
}

// #endregion Album's first and last songs

// #region Check changes on saved albums

export const checkChangesOnSavedAlbums = (savedAlbums) => {
    return new Promise((resolve, reject) => {
        let albumsToAdd = [];
    
        const getAlbumsLoop = (nextPageUrl) => {
            if(!nextPageUrl)
            {
                return;
            }

            getSavedAlbums(nextPageUrl)
                .then((result) => {
                    const recentAlbums = result.items;
                    let newAlbums = [];
    
                    const lastAddedAtTime = loadLastAddedAlbumTime();
                    recentAlbums.forEach((album) => {
                        const addedAtMS = Date.parse(album.added_at);
                        
                        if(addedAtMS > lastAddedAtTime)
                        {
                            if(!savedAlbums.some((savedAlbum) => savedAlbum.id === album.album.id))
                            {
                                newAlbums = [...newAlbums, album]
                            }
                        }
                    })
    
                    if(newAlbums.length > 0)
                    {
                        albumsToAdd = [...albumsToAdd, ...newAlbums];
                        return getAlbumsLoop(result.next);
                    }
    
                    resolve({
                        areAlbumsToAdd: albumsToAdd.length > 0,
                        areDeletedAlbums: savedAlbums.length + albumsToAdd.length !== result.total
                    });
                })
                .catch((error) => reject(error));            
        }   
    
        getAlbumsLoop(getSavedAlbumsRequestUrl(CHECK_NEW_ALBUMS_BLOCK_LENGTH));
    })
}

// #endregion Check changes on saved albums

// #endregion Complex methods

// #region getAlbum

const getAlbumRequest = (albumId) => {
    const endpoint = `/albums/${albumId}`;

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () => undefined;

    const method = 'GET';
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getAlbum = (albumId) => {
    const request = getAlbumRequest(albumId);

    return resolve(request);
}

// #endregion getAlbum

// #region getSavedAlbums

export const getSavedAlbumsRequestUrl = (limit, offset, market) => {
    const endpoint = "/me/albums";

    let result = `${BASE_URL}${endpoint}?limit=${limit}`;

    if(offset) result = `${result}&offset=${offset}`;
    if(market) result = `${result}&market=${market}`;

    return result;
}

const getSavedAlbumsRequest = (nextPageUrl) => {
    const buildUrl = () => nextPageUrl;
    const buildBody = () => undefined;

    const method = "GET";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getSavedAlbums = (nextPageUrl) => {
    const request = getSavedAlbumsRequest(nextPageUrl);

    return resolve(request);
}

// #endregion getSavedAlbums

// #region getPlaybackState

const getPlaybackStateRequest = (includePodcast = true) => {
    const endpoint = "/me/player";

    const buildUrl = () => {
        let result = `${BASE_URL}${endpoint}`;

        if(includePodcast) {
            result = `${result}?additional_types=episode`;
        }

        return result;
    }
    const buildBody = () => undefined;

    const method = "GET";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getPlaybackState = (includePodcast = true) => {
    const request = getPlaybackStateRequest(includePodcast);

    return resolve(request);
}

// #endregion getPlaybackState

// #region getAvailableDevices

const getAvailableDevicesRequest = () => {
    const endpoint = "/me/player/devices";

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () => undefined;

    const method = "GET";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getAvailableDevices = () => {
    const request = getAvailableDevicesRequest();

    return resolve(request);
}

// #endregion getAvailableDevices

// #region getQueue

const getQueueRequest = () => {
    const endpoint = "/me/player/queue";

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () => undefined;

    const method = "GET";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getQueue = () => {
    const request = getQueueRequest();

    return resolve(request);
}

// #endregion getQueue

// #region getCurrentUserProfile

const getCurrentUserProfileRequest = () => {
    const endpoint = "/me";

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () => undefined;

    const method = "GET";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const getCurrentUserProfile = () => {
    const request = getCurrentUserProfileRequest();

    return resolve(request);
}

// #endregion getCurrentUserProfile

// #region postItemToQueue

const postItemToQueueRequest = (itemUri, deviceId) => {
    const endpoint = "/me/player/queue";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}?uri=${itemUri}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}&device_id=${deviceId}`;
        }

        return finalUrl;
    };
    const buildBody = () => undefined;

    const method = "POST";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const postItemToQueue = (itemUri, deviceId) => {
    const request = postItemToQueueRequest(itemUri, deviceId);

    return resolve(request);
}

// #endregion postItemToQueue

// #region postPreviousItem

const postPreviousItemRequest = (deviceId) => {
    const endpoint = "/me/player/previous";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}?device_id=${deviceId}`;
        }

        return finalUrl;
    };
    const buildBody = () => undefined;

    const method = "POST";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const postPreviousItem = (deviceId) => {
    const request = postPreviousItemRequest(deviceId);

    return resolve(request);
}

// #endregion postPreviousItem

// #region postNextItem

const postNextItemRequest = (deviceId) => {
    const endpoint = "/me/player/next";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}?device_id=${deviceId}`;
        }

        return finalUrl;
    };
    const buildBody = () => undefined;

    const method = "POST";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const postNextItem = (deviceId) => {
    const request = postNextItemRequest(deviceId);
    
    return resolve(request);
}

// #endregion postNextItem

// #region putPausePlayback

const putPausePlaybackRequest = (deviceId) => {
    const endpoint = "/me/player/pause";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}?device_id=${deviceId}`;
        }

        return finalUrl;
    };
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putPausePlayback = (deviceId) => {
    const request = putPausePlaybackRequest(deviceId);

    return resolve(request);
}

// #endregion putPausePlayback

// #region putResumePlayback

const putResumePlaybackRequest = (deviceId) => {
    const endpoint = "/me/player/play";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}?device_id=${deviceId}`;
        }

        return finalUrl;
    };
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putResumePlayback = async (deviceId) => {
    const request = putResumePlaybackRequest(deviceId);

    return resolve(request);
}

// #endregion putResumePlayback

// #region putPlayItem

const putPlayItemRequest = (itemId, itemType, deviceId, offset, positionMs) => {
    const endpoint = '/me/player/play';

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}?device_id=${deviceId}`;
        }

        return finalUrl;
    };

    const buildBody = () => {
        if(!itemType) itemType = getItemType(itemId);

        if(!itemType) console.error("Item type not identified");

        const fullItemId = buildFullItemId(itemId, itemType);

        let body = {};
        if(itemType === ITEM_TYPES.track)
        {
            body.uris = [ fullItemId ];
        }
        else
        {
            body.context_uri = fullItemId;
            if(offset)
            {
                body.offset = offset;
            }

            if(positionMs)
            {
                body.position_ms = positionMs;
            }
        }

        return JSON.stringify(body);
    }

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putPlayItem = async (itemId, itemType, deviceId, offset, positionMs) => {
    const request = putPlayItemRequest(itemId, itemType, deviceId, offset, positionMs);

    return resolve(request);
}

// #endregion putPlayItem

// #region putActiveDevice

export const putActiveDeviceRequest = (deviceId, play) => {
    const endpoint = "/me/player";

    const buildUrl = () => `${BASE_URL}${endpoint}`;
    const buildBody = () => JSON.stringify({
        device_ids: [deviceId],
        play: play
    });

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putActiveDevice = (deviceId, play = false) => {
    const request = putActiveDeviceRequest(deviceId, play);

    return resolve(request);
}

// #endregion putActiveDevice

// #region putRepeatMode

const putRepeatModeRequest = (repeatMode, deviceId) => {
    const endpoint = "/me/player/repeat";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}?state=${repeatMode}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}&device_id=${deviceId}`;
        }

        return finalUrl;
    }
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putRepeatMode = (repeatMode, deviceId) => {
    const request = putRepeatModeRequest(repeatMode, deviceId);

    return resolve(request);
}

// #endregion putRepeatMode

// #region putShuffleMode

const putShuffleModeRequest = (shuffleMode, deviceId) => {
    const endpoint = "/me/player/shuffle";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}?state=${shuffleMode}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}&device_id=${deviceId}`;
        }

        return finalUrl;
    }
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putShuffleMode = (shuffleMode, deviceId) => {
    const request = putShuffleModeRequest(shuffleMode, deviceId);

    return resolve(request);
}

// #endregion putShuffleMode

// #region putItemProgress

const putItemProgressRequest = (positionMS, deviceId) => {
    const endpoint = "/me/player/seek";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}?position_ms=${positionMS}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}&device_id=${deviceId}`;
        }

        return finalUrl;
    }
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putItemProgress = (positionMS, deviceId) => {
    const request = putItemProgressRequest(positionMS, deviceId);

    return resolve(request);
}

// #endregion putItemProgress

// #region putPlaybackVolume

const putPlaybackVolumeRequest = (volume, deviceId) => {
    const endpoint = "/me/player/volume";

    const buildUrl = () => {
        let finalUrl = `${BASE_URL}${endpoint}?volume_percent=${volume}`;

        if(deviceId)
        {
            finalUrl = `${finalUrl}&device_id=${deviceId}`;
        }

        return finalUrl;
    }
    const buildBody = () => undefined;

    const method = "PUT";
    const url = buildUrl();
    const body = buildBody();

    return { method, url, body };
}

export const putPlaybackVolume = (volume, deviceId) => {
    const request = putPlaybackVolumeRequest(volume, deviceId);

    return resolve(request);
}

// #endregion putPlaybackVolume

// #region Generic methods

const resolve = async (request, innerDebug) => {
    await checkRefreshToken();

    const { method, url, body } = request;

    const payload = {
        method: method,
        headers: {
            Authorization: `Bearer ${loadAccessToken()}`,
            "Content-Type": "application/json"
        },
        body: body
    }

    return new Promise((resolve, reject) => {
        if(DEBUG && innerDebug) console.log(url, payload);

        if(loadTooManyRequestsBlocking())
        {
            if(DEBUG && innerDebug) reject({ status: 629, ok: false }); // I used 629 and no 429, because it would cause a too many requests blocking endless loop
            return;
        }

        fetch(url, payload)
            .then((response) => {
                if(DEBUG && innerDebug) console.log(response);

                if(!response.ok) {
                    reject(response);
                    return;
                }

                const contentType = response.headers.get('content-type');
                return contentType && contentType.indexOf('application/json') !== -1 ? response.json() : response;
            })
            .then((data) => {
                if(DEBUG && innerDebug) console.log(data);
                return resolve(data);
            })
            .catch((error) => { 
                if(DEBUG && innerDebug) console.log(error);
                return reject(error);
            })
    });
}

// #endregion Generic methods